import { useMemo, useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import { Text, Colors } from '@mercator-gcp/grout';
import SectionWrapper from 'components/widgets/SearchFilterWizard/steps/components/SectionWrapper';
import SelectPillButton from 'components/widgets/SearchFilterWizard/steps/components/SelectPillButton';
import { useAvailableCities } from 'hooks/useUserCityAccess';
import { useUserStateSelection, useUserMultiRegionSelection } from 'state/dashboard';
import {
  State,
  STATE_CODE_TO_LABEL,
  Region,
  METROPOLITAN_CODE_TO_LABEL,
} from 'utils/constants/location';

const sectionStyle = {
  marginTop: 'padding_8',
  marginBottom: 'padding_16',
  '@media (max-width: 1280px)': {
    marginTop: 'padding_8',
    marginBottom: 'padding_12',
  },
  '@media (max-width: 1024px)': {
    marginTop: 'padding_8',
    marginBottom: 'padding_8',
  },
};

const sectionLabelStyle = {
  color: Colors.ArmyGreen900,
  textTransform: 'uppercase',
  marginBottom: 'padding_16',
  display: 'block',
  '@media (max-width: 1280px)': {
    marginBottom: 'padding_12',
  },
  '@media (max-width: 1024px)': {
    marginBottom: 'padding_8',
  },
};

const stateButtonContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'padding_12',
  marginBottom: 'padding_16',
  '@media (max-width: 1280px)': {
    gap: 'padding_8',
    marginBottom: 'padding_12',
  },
  '@media (max-width: 1024px)': {
    gap: 'padding_8',
    marginBottom: 'padding_8',
  },
};

const regionTagContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'padding_12',
  '@media (max-width: 1280px)': {
    gap: 'padding_8',
  },
  '@media (max-width: 1024px)': {
    gap: 'padding_8',
  },
};

type GeographicFocusStepProps = {
  selectedState: string | null;
  onStateChange: (state: string | null) => void;
  selectedRegions?: string[];
  onRegionsChange?: (regions: string[]) => void;
};

const GeographicFocusStep = ({
  selectedState,
  onStateChange,
  selectedRegions = [],
  onRegionsChange,
}: GeographicFocusStepProps) => {
  const [, setState] = useUserStateSelection();
  const [, setRegions] = useUserMultiRegionSelection();
  const { availableStates } = useAvailableCities();

  const regionOptions = useMemo(() => {
    if (!selectedState || !availableStates[selectedState as State]) {
      return [];
    }

    const regionsForState = availableStates[selectedState as State];

    return Object.keys(regionsForState.regions).map((key: string) => ({
      id: key,
      value: key,
      label: METROPOLITAN_CODE_TO_LABEL[key] || key,
    }));
  }, [availableStates, selectedState]);

  const handleStateChange = (value: string) => {
    onStateChange(value);
    setState(value as State);
    // Auto-select all regions for the selected state
    if (onRegionsChange && availableStates[value as State]) {
      const regionsForState = availableStates[value as State];
      const allRegionValues = Object.keys(regionsForState.regions);
      onRegionsChange(allRegionValues);
      setRegions(allRegionValues as Region[]);
    }
  };

  const handleRegionToggle = (regionId: string) => {
    if (!onRegionsChange || !selectedState) return;

    let newRegions = selectedRegions.includes(regionId)
      ? selectedRegions.filter((r) => r !== regionId)
      : [...selectedRegions, regionId];

    // Users cannot have zero regions selected; if they deselect the last one, select all for state
    if (newRegions.length === 0 && availableStates[selectedState as State]) {
      const regionsForState = availableStates[selectedState as State];
      newRegions = Object.keys(regionsForState.regions);
    }

    onRegionsChange(newRegions);
    setRegions(newRegions as Region[]);
  };

  // Filter state options based on user's region permissions
  const stateOptions = useMemo(() => {
    return Object.keys(availableStates).filter((state) => {
      // Only show states that the user has access to
      return (
        availableStates[state as State] &&
        Object.keys(availableStates[state as State].regions).length > 0
      );
    }) as State[];
  }, [availableStates]);

  // Auto-select state if user only has access to one state
  useEffect(() => {
    if (stateOptions.length === 1 && !selectedState && stateOptions[0]) {
      const singleState = stateOptions[0];
      onStateChange(singleState);
      setState(singleState as State);
      // Auto-select all regions for the selected state
      if (onRegionsChange && availableStates[singleState as State]) {
        const regionsForState = availableStates[singleState as State];
        const allRegionValues = Object.keys(regionsForState.regions);
        onRegionsChange(allRegionValues);
        setRegions(allRegionValues as Region[]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateOptions.length, selectedState]); // Only check length and selectedState to avoid infinite loops

  return (
    <SectionWrapper>
      {/* State Selection */}
      <Box sx={sectionStyle}>
        <Box sx={stateButtonContainerStyle}>
          {stateOptions.map((state) => (
            <SelectPillButton
              key={state}
              isSelected={selectedState === state}
              onClick={() => handleStateChange(state)}
            >
              {STATE_CODE_TO_LABEL[state]}
            </SelectPillButton>
          ))}
        </Box>
      </Box>

      {/* Region Selection */}
      {selectedState && regionOptions.length > 0 && (
        <Box sx={sectionStyle}>
          <Text.BodyBold sx={sectionLabelStyle}>KEY REGIONS</Text.BodyBold>
          <Box sx={regionTagContainerStyle}>
            {regionOptions.map((region) => (
              <SelectPillButton
                key={region.id}
                isSelected={selectedRegions.includes(region.value)}
                onClick={() => handleRegionToggle(region.value)}
              >
                {region.label}
              </SelectPillButton>
            ))}
          </Box>
        </Box>
      )}
    </SectionWrapper>
  );
};

export default GeographicFocusStep;
